/**
 * NPC System Types
 * 
 * Defines types for NPC locomotion, animation state, and behavior.
 * NPCs combine movement, rotation, and animation seamlessly.
 */

import { Vector3 } from "@babylonjs/core/Maths/math.vector";

/**
 * Movement state for an NPC
 */
export enum NPCMovementState {
    IDLE = "idle",
    WALKING = "walking",
    RUNNING = "running",
    SPRINTING = "sprinting",
    JUMPING = "jumping",
    FALLING = "falling",
    LANDING = "landing",
    CROUCHING = "crouching",
    CRAWLING = "crawling",
    SWIMMING = "swimming",
    CLIMBING = "climbing",
    FLYING = "flying",
}

/**
 * Action state for an NPC (overlays movement)
 */
export enum NPCActionState {
    NONE = "none",
    ATTACKING = "attacking",
    BLOCKING = "blocking",
    INTERACTING = "interacting",
    PICKING_UP = "picking_up",
    THROWING = "throwing",
    EATING = "eating",
    DRINKING = "drinking",
    SLEEPING = "sleeping",
    SITTING = "sitting",
    DYING = "dying",
    DEAD = "dead",
}

/**
 * Expression/emotion state
 */
export enum NPCEmotionState {
    NEUTRAL = "neutral",
    HAPPY = "happy",
    SAD = "sad",
    ANGRY = "angry",
    SCARED = "scared",
    SURPRISED = "surprised",
    CURIOUS = "curious",
    TIRED = "tired",
    ALERT = "alert",
}

/**
 * Animation mapping - maps states to animation names
 */
export interface NPCAnimationMap {
    // Movement animations
    idle?: string | string[];
    walk?: string | string[];
    run?: string | string[];
    sprint?: string | string[];
    jump?: string;
    fall?: string;
    land?: string;
    crouch?: string;
    crouchWalk?: string;
    crawl?: string;
    swim?: string;
    climb?: string;
    fly?: string;

    // Action animations
    attack?: string | string[];
    block?: string;
    interact?: string;
    pickUp?: string;
    throw?: string;
    eat?: string;
    drink?: string;
    sleep?: string;
    sit?: string;
    die?: string;
    dead?: string;

    // Emotion/expression animations (often facial or subtle body language)
    emoteHappy?: string;
    emoteSad?: string;
    emoteAngry?: string;
    emoteScared?: string;
    emoteSurprised?: string;
    emoteCurious?: string;

    // Custom animations by name
    [key: string]: string | string[] | undefined;
}

/**
 * Locomotion settings
 */
export interface NPCLocomotionConfig {
    /** Walk speed in units/second */
    walkSpeed: number;
    /** Run speed in units/second */
    runSpeed: number;
    /** Sprint speed in units/second */
    sprintSpeed: number;
    /** Rotation speed in radians/second */
    rotationSpeed: number;
    /** Acceleration rate */
    acceleration: number;
    /** Deceleration rate */
    deceleration: number;
    /** Whether to smoothly interpolate rotation */
    smoothRotation: boolean;
    /** Minimum velocity to trigger walk animation */
    walkThreshold: number;
    /** Minimum velocity to trigger run animation */
    runThreshold: number;
    /** Gravity applied when not grounded */
    gravity: number;
    /** Jump force */
    jumpForce: number;
}

/**
 * Movement input for an NPC
 */
export interface NPCMovementInput {
    /** Forward/backward input (-1 to 1) */
    forward: number;
    /** Right/left strafe input (-1 to 1) */
    right: number;
    /** Whether to run instead of walk */
    run: boolean;
    /** Whether to sprint */
    sprint: boolean;
    /** Jump request */
    jump: boolean;
    /** Crouch toggle */
    crouch: boolean;
}

/**
 * Target-based movement for AI
 */
export interface NPCMoveToTarget {
    /** Target position */
    position: Vector3;
    /** Stop distance from target */
    stopDistance: number;
    /** Move at run speed? */
    run: boolean;
    /** Callback when reached */
    onReached?: () => void;
    /** Callback when path blocked */
    onBlocked?: () => void;
}

/**
 * Patrol waypoint
 */
export interface NPCPatrolPoint {
    /** Position */
    position: Vector3;
    /** Wait time at this point (seconds) */
    waitTime: number;
    /** Whether to run to this point (default: false = walk) */
    run?: boolean;
    /** Optional animation to play while waiting */
    waitAnimation?: string;
    /** Optional callback when reached */
    onReached?: () => void;
}

/**
 * NPC Controller configuration
 */
export interface NPCControllerConfig {
    /** Animation name mapping */
    animations: NPCAnimationMap;
    /** Locomotion settings */
    locomotion: Partial<NPCLocomotionConfig>;
    /** Initial movement state */
    initialState?: NPCMovementState;
    /** Enable root motion from animations */
    useRootMotion?: boolean;
    /** Sync animation speed with movement speed */
    syncAnimationSpeed?: boolean;
    /** Default blend time between animations (seconds) */
    blendTime?: number;
    /** Callback to get ground height at a position (for terrain following) */
    getGroundHeight?: (x: number, z: number) => number;
}

/**
 * NPC state snapshot for serialization/debugging
 */
export interface NPCStateSnapshot {
    movementState: NPCMovementState;
    actionState: NPCActionState;
    emotionState: NPCEmotionState;
    position: { x: number; y: number; z: number };
    rotation: number;
    velocity: { x: number; y: number; z: number };
    currentAnimation: string | null;
    isGrounded: boolean;
    moveTarget: { x: number; y: number; z: number } | null;
}

/**
 * Event types emitted by NPC controller
 */
export type NPCEventType = 
    | "stateChange"
    | "actionStart"
    | "actionEnd"
    | "emotionChange"
    | "reachedTarget"
    | "pathBlocked"
    | "animationStart"
    | "animationEnd"
    | "footstep";

/**
 * NPC event data
 */
export interface NPCEvent {
    type: NPCEventType;
    previousState?: string;
    currentState?: string;
    data?: unknown;
}

/**
 * NPC event handler
 */
export type NPCEventHandler = (event: NPCEvent) => void;
